const DB_NAME = "apparify-rider-tracking-v1";
const DB_VERSION = 1;
const SAMPLE_STORE = "samples";
const META_STORE = "sessionMeta";
export const TRACKING_QUEUE_MAX_SAMPLES = 120;
export const TRACKING_QUEUE_MAX_BYTES = 1024 * 1024;
export const TRACKING_QUEUE_MAX_AGE_MS = 10 * 60 * 1000;

function queueError(message, code) {
  const error = new Error(message);
  error.code = code;
  return error;
}

function openDatabase() {
  if (!window.indexedDB) return Promise.reject(queueError("Offline storage is unavailable.", "TRACKING_STORAGE_UNAVAILABLE"));
  return new Promise((resolve, reject) => {
    const request = window.indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(SAMPLE_STORE)) {
        const store = db.createObjectStore(SAMPLE_STORE, { keyPath: "queueId" });
        store.createIndex("bySessionSequence", ["sessionId", "sequenceNo"], { unique: true });
      }
      if (!db.objectStoreNames.contains(META_STORE)) db.createObjectStore(META_STORE, { keyPath: "sessionId" });
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(queueError("Offline storage could not be opened.", "TRACKING_STORAGE_UNAVAILABLE"));
  });
}

function requestPromise(request) {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error || queueError("Offline storage failed.", "TRACKING_STORAGE_ERROR"));
  });
}

function transactionDone(transaction) {
  return new Promise((resolve, reject) => {
    transaction.oncomplete = () => resolve();
    transaction.onabort = () => reject(transaction.error || queueError("Offline storage was interrupted.", "TRACKING_STORAGE_ERROR"));
    transaction.onerror = () => reject(transaction.error || queueError("Offline storage failed.", "TRACKING_STORAGE_ERROR"));
  });
}

function byteSize(value) {
  return new TextEncoder().encode(JSON.stringify(value)).byteLength;
}

export async function listTrackingSamples(sessionId, now = Date.now()) {
  const db = await openDatabase();
  try {
    const tx = db.transaction(SAMPLE_STORE, "readwrite");
    const store = tx.objectStore(SAMPLE_STORE);
    const rows = await requestPromise(store.getAll());
    const matching = rows
      .filter((row) => Number(row.sessionId) === Number(sessionId))
      .sort((left, right) => left.sequenceNo - right.sequenceNo);
    const valid = [];
    for (const row of matching) {
      if (now - new Date(row.sample.capturedAt).getTime() > TRACKING_QUEUE_MAX_AGE_MS) store.delete(row.queueId);
      else valid.push(row);
    }
    await transactionDone(tx);
    return valid;
  } finally {
    db.close();
  }
}

export async function allocateTrackingSequence(sessionId) {
  const db = await openDatabase();
  try {
    const tx = db.transaction(META_STORE, "readwrite");
    const store = tx.objectStore(META_STORE);
    const current = await requestPromise(store.get(Number(sessionId)));
    const sequenceNo = Number(current?.nextSequence || 1);
    store.put({ sessionId: Number(sessionId), nextSequence: sequenceNo + 1 });
    await transactionDone(tx);
    return sequenceNo;
  } finally {
    db.close();
  }
}

export async function enqueueTrackingSample(sessionId, sample, now = Date.now()) {
  const valid = await listTrackingSamples(sessionId, now);
  const record = {
    queueId: `${Number(sessionId)}:${sample.sequenceNo}`,
    sessionId: Number(sessionId),
    sequenceNo: sample.sequenceNo,
    sample,
    bytes: byteSize(sample),
  };
  const totalBytes = valid.reduce((sum, row) => sum + Number(row.bytes || byteSize(row.sample)), 0);
  if (valid.length >= TRACKING_QUEUE_MAX_SAMPLES || totalBytes + record.bytes > TRACKING_QUEUE_MAX_BYTES) {
    throw queueError("Tracking storage is full. Reconnect before collecting more points.", "TRACKING_QUEUE_FULL");
  }
  const db = await openDatabase();
  try {
    const tx = db.transaction(SAMPLE_STORE, "readwrite");
    tx.objectStore(SAMPLE_STORE).add(record);
    await transactionDone(tx);
    return { count: valid.length + 1, bytes: totalBytes + record.bytes };
  } finally {
    db.close();
  }
}

export async function removeTrackingSamples(sessionId, sequenceNumbers) {
  const wanted = new Set(sequenceNumbers.map(Number));
  const db = await openDatabase();
  try {
    const tx = db.transaction(SAMPLE_STORE, "readwrite");
    const store = tx.objectStore(SAMPLE_STORE);
    const rows = await requestPromise(store.getAll());
    rows.forEach((row) => {
      if (Number(row.sessionId) === Number(sessionId) && wanted.has(Number(row.sequenceNo))) store.delete(row.queueId);
    });
    await transactionDone(tx);
  } finally {
    db.close();
  }
}

export async function clearTrackingSessionQueue(sessionId) {
  const rows = await listTrackingSamples(sessionId);
  await removeTrackingSamples(sessionId, rows.map((row) => row.sequenceNo));
}
